// Kreator rejestracji operatora — 7 kroków:
// dane firmy → subdomena → branże → plan → konto administratora → płatności → podsumowanie.

import { useEffect, useMemo, useState } from "react";
import { Button, Card, Checkbox, Chip, Icon, TextInput, Tile, TogglePill, WizardStepper } from "@b00k/ui";
import type { IconName } from "@b00k/ui";
import { CAT_COLOR, validateSignupStep } from "@b00k/domain";
import { useT } from "../i18n/index.js";
import { get, post } from "../lib/api.js";
import type { ApiError } from "../lib/api.js";
import { wartoscKonfiguracji } from "../lib/config.js";
import { poPowrocie } from "../lib/stan-firmy.js";
import type { AkcjaFirmy, StanFirmy, StatusLogowaniaFirmy, WyborLogowania } from "../lib/stan-firmy.js";
import type { Lang, TierId } from "@b00k/domain";

interface Industry {
  key: string;
  label: string;
  labelEn: string | null;
  type: "time" | "day";
  unit: string | null;
}

/**
 * Kształt progu z `/public/plans`.
 *
 * Cechy progu (`t1f1`…) są po stronie interfejsu, nie API: to tekst marketingowy,
 * który zmienia się razem z landingiem i musi być w języku, który użytkownik
 * właśnie przełączył — a nie w tym, który wysłał serwer przy pierwszym żądaniu.
 */
interface Plan {
  id: TierId;
  range: string;
  price: number | null;
  formatted: string | null;
  recommended?: boolean;
}

interface SlugState {
  checking: boolean;
  available: boolean | null;
  reason: "invalid" | "taken" | null;
  host: string;
}

const ICON_FOR: Record<string, IconName> = {
  rooms: "bed",
  cars: "car",
  attr: "ticket",
  hair: "scissors",
  beauty: "sparkles",
  med: "med",
  workshop: "wrench",
  tutoring: "book",
  other: "plus",
};

/** Formularz przeżywa wyjście do Keycloaka i powrót — bez haseł. */
const SCHOWEK = "b00k.rejestracja.formularz";

const EXTRA_COLOR: Record<string, string> = { workshop: "#C8930A", tutoring: "#0F8A6E", other: "#6B6F76" };

const ROOT_DOMAIN = wartoscKonfiguracji("rootDomain", import.meta.env.VITE_ROOT_DOMAIN, "b00k.online");
const PAY_METHODS = [
  { id: "blik", label: "BLIK" },
  { id: "card", label: "KARTA" },
  { id: "transfer", label: "PRZELEW" },
];

export function SignupWizardPage({ lang }: { lang: Lang }) {
  const t = useT();
  const [step, setStep] = useState(0);
  const [maxReached, setMaxReached] = useState(0);
  const [industries, setIndustries] = useState<Industry[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState("");
  const [done, setDone] = useState<{ host: string; operatorUrl: string; bookingUrl: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const [form, setForm] = useState({
    companyName: "",
    nip: "",
    phone: "",
    address: "",
    slug: "",
    industries: [] as string[],
    plan: "TEAM" as Plan["id"],
    admin: { name: "", email: "", pass: "", pass2: "", terms: false },
    payments: { enabled: false, methods: ["blik", "card", "transfer"] as string[] },
  });

  const [slug, setSlug] = useState<SlugState>({ checking: false, available: null, reason: null, host: "" });
  /** Firma ma konto w b00k albo NO-HUMAN ID — komunikat i przycisk zamiast „Dalej”. */
  const [firma, setFirma] = useState<StanFirmy | null>(null);
  const [wybor, setWybor] = useState<WyborLogowania[] | null>(null);
  /**
   * Administrator firmy potwierdzony logowaniem. Z konta firmy — konto już
   * jest, bez hasła; kontem panelu klienta — hasło do panelu b00k ustawia tu.
   */
  const [dowod, setDowod] = useState<{ email: string; rodzaj: WyborLogowania["rodzaj"] } | null>(null);
  const bezHasla = dowod !== null && dowod.rodzaj !== "klienci";

  useEffect(() => {
    get<{ items: Industry[] }>("/public/industries").then((r) => setIndustries(r.items)).catch(() => setIndustries([]));
    get<{ items: Plan[] }>(`/public/plans?lang=${lang}`).then((r) => setPlans(r.items)).catch(() => setPlans([]));
  }, []);

  // Powrót z logowania administratora firmy: odtwarzamy formularz i pytamy API o werdykt.
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("firma") !== "1") return;
    try {
      const zapisany = sessionStorage.getItem(SCHOWEK);
      if (zapisany) setForm((f) => ({ ...f, ...(JSON.parse(zapisany) as Partial<typeof f>), admin: { ...f.admin } }));
      sessionStorage.removeItem(SCHOWEK);
    } catch {
      // Bez schowka człowiek wpisze dane jeszcze raz — logowanie i tak się liczy.
    }
    window.history.replaceState(null, "", window.location.pathname);
    get<StatusLogowaniaFirmy>(`/public/register/company-login/status?lang=${lang}`)
      .then((r) => {
        const w = poPowrocie(r, t.companyLoginOther);
        if (w.kind === "dowod") {
          setDowod({ email: w.email, rodzaj: w.rodzaj });
          setFirma(null);
          setForm((f) => ({ ...f, admin: { ...f.admin, email: w.email, pass: "", pass2: "" } }));
        } else if (w.kind === "firma") {
          // Bez roli: komunikat i ponowne logowanie innym kontem (wybór konta firmy / panelu klienta).
          setWybor(null);
          setFirma(w.firma);
        }
      })
      .catch(() => undefined);
  }, []);

  // Dostępność subdomeny sprawdzamy online, z odbiciem 400 ms.
  useEffect(() => {
    if (step !== 1 || !form.slug) {
      setSlug({ checking: false, available: null, reason: null, host: "" });
      return;
    }
    setSlug((s) => ({ ...s, checking: true }));
    const id = setTimeout(() => {
      get<{ available: boolean; reason: "invalid" | "taken" | null; host?: string }>(
        `/public/subdomain-available?slug=${encodeURIComponent(form.slug)}`,
      )
        .then((r) => setSlug({ checking: false, available: r.available, reason: r.reason, host: r.host ?? "" }))
        .catch(() => setSlug({ checking: false, available: null, reason: null, host: "" }));
    }, 400);
    return () => clearTimeout(id);
  }, [form.slug, step]);

  const steps = useMemo(
    () => [
      { id: "company", label: t.wizStep1 },
      { id: "slug", label: t.wizStep2 },
      { id: "industries", label: t.wizStep3 },
      { id: "plan", label: t.wizStep4 },
      { id: "admin", label: t.wizStep5 },
      { id: "payments", label: t.wizStep6 },
      { id: "summary", label: t.wizStep7 },
    ],
    [t],
  );

  const goNext = async () => {
    // Kroki 1–5 mają walidację domenową; 6 jest opcjonalny, 7 to podsumowanie.
    const errs = validateSignupStep(step + 1, form, lang);
    if (step === 1 && slug.available === false) errs.slug = slug.reason === "taken" ? t.slugTaken : t.slugInvalid;
    if (step === 4 && dowod) {
      // Adres z logowania. Konto firmy ma już hasło; po logowaniu kontem
      // panelu klienta hasło do panelu b00k ustawia się tutaj.
      delete errs.email;
      if (bezHasla) {
        delete errs.pass;
        delete errs.pass2;
      }
    }
    setErrors(errs);
    if (Object.keys(errs).length) return;
    if (step === 0 && !dowod) {
      // Stan firmy po NIP-ie. Niedostępne API nie blokuje kreatora — rozstrzyga
      // i tak serwer przy zakładaniu konta.
      setWybor(null);
      const stan = await get<StanFirmy>(`/public/company-state?nip=${encodeURIComponent(form.nip)}&lang=${lang}`).catch(
        () => null,
      );
      if (stan && stan.stan !== "nowa") {
        setFirma(stan);
        return;
      }
      setFirma(null);
    }
    const next = Math.min(steps.length - 1, step + 1);
    setStep(next);
    setMaxReached((m) => Math.max(m, next));
  };

  /** „Zaloguj się jako administrator firmy” — i wybór konta, gdy są dwa. */
  const zalogujAdministratora = async (rodzaj?: WyborLogowania["rodzaj"]) => {
    setBusy(true);
    try {
      const { admin: _bezHasel, ...reszta } = form;
      try {
        sessionStorage.setItem(SCHOWEK, JSON.stringify(reszta));
      } catch {
        // Prywatne okno bez schowka — formularz trzeba będzie uzupełnić po powrocie.
      }
      const r = await post<{ url?: string; wybor?: WyborLogowania[] }>(`/public/register/company-login?lang=${lang}`, {
        nip: form.nip,
        ...(rodzaj ? { rodzaj } : {}),
      });
      if (r.wybor) setWybor(r.wybor);
      else if (r.url) window.location.assign(r.url);
    } catch (e) {
      setFirma({ stan: "realm_bez_produktu", message: (e as ApiError).message || t.errGeneric });
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    setBusy(true);
    setSubmitError("");
    try {
      const res = await post<{ host: string; operatorUrl: string; bookingUrl: string }>("/public/operators", form);
      setDone(res);
    } catch (e) {
      const err = e as ApiError;
      if (err.code === "company_known" || err.code === "company_in_product") {
        // Firma ma już konto — wracamy do pierwszego kroku z komunikatem i przyciskiem.
        const d = err.details as { stan: StanFirmy["stan"]; action?: AkcjaFirmy } | undefined;
        setFirma({ stan: d?.stan ?? "realm_bez_produktu", message: err.message, ...(d?.action ? { action: d.action } : {}) });
        setDowod(null);
        setStep(0);
        return;
      }
      if (err.details && typeof err.details === "object") setErrors(err.details as Record<string, string>);
      setSubmitError(err.message || t.errGeneric);
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <main className="ld-wizard">
        <Card>
          <div className="ld-done">
            <span style={{ color: "var(--accent)" }}>
              <Icon name="check" size={32} />
            </span>
            <h1 style={{ fontWeight: 900, fontSize: 32, letterSpacing: "-1.2px" }}>{t.doneTitle}</h1>
            <p style={{ margin: 0, color: "var(--text-muted)", lineHeight: 1.6 }}>{t.doneSub}</p>
            <Chip variant="accent">{done.host.toUpperCase()}</Chip>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <a href={done.operatorUrl}>
                <Button arrow>{t.doneOpen}</Button>
              </a>
              <a href={done.bookingUrl}>
                <Button variant="ghost">{t.doneBooking}</Button>
              </a>
            </div>
          </div>
        </Card>
      </main>
    );
  }

  const setAdmin = (patch: Partial<typeof form.admin>) => setForm({ ...form, admin: { ...form.admin, ...patch } });

  return (
    <main className="ld-wizard">
      <div className="ld-wizard__intro">
        <div className="ld-kicker">{t.catsKick}</div>
        <h1>{t.wizTitle}</h1>
        <p>{t.wizSub}</p>
      </div>

      <WizardStepper
        steps={steps}
        current={step}
        maxReached={maxReached}
        onGoTo={(i) => setStep(i)}
        footer={
          <>
            {step > 0 ? (
              <Button variant="ghost" onClick={() => setStep(step - 1)}>
                {t.wizBack}
              </Button>
            ) : null}
            {step < steps.length - 1 ? (
              <Button onClick={() => void goNext()} disabled={step === 0 && firma !== null && !dowod} arrow>
                {t.wizNext}
              </Button>
            ) : (
              <Button onClick={() => void submit()} disabled={busy} arrow>
                {t.wizFinish}
              </Button>
            )}
            {submitError ? <span className="ui-field__error">{submitError}</span> : null}
          </>
        }
      >
        <Card>
          {step === 0 ? (
            <div className="ld-fields">
              <TextInput
                label={t.fCompany}
                value={form.companyName}
                error={errors.companyName}
                onChange={(e) => setForm({ ...form, companyName: e.target.value })}
              />
              <TextInput
                label={t.fNip}
                mono
                placeholder="525-234-40-78"
                value={form.nip}
                error={errors.nip}
                onChange={(e) => {
                  setForm({ ...form, nip: e.target.value });
                  setFirma(null);
                  setWybor(null);
                  setDowod(null);
                }}
              />
              <TextInput
                label={t.fPhone}
                mono
                placeholder="+48 75 000 00 00"
                value={form.phone}
                error={errors.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
              <TextInput
                label={t.fAddress}
                value={form.address}
                error={errors.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
              />
              {dowod ? (
                <span className="ld-slug__status" data-ok="true" data-testid="firma-dowod">
                  {t.companyProofOk} <span className="b00k-mono">{dowod.email}</span>
                </span>
              ) : null}
              {firma && !dowod ? (
                <div className="ld-firma" role="status" data-testid="firma-stan" data-stan={firma.stan} data-werdykt={firma.werdykt}>
                  {firma.message ? <span className="ui-field__error">{firma.message}</span> : null}
                  <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                    {firma.action?.kind === "login" ? (
                      <a href={firma.action.href}>
                        <Button arrow>{firma.action.label}</Button>
                      </a>
                    ) : null}
                    {firma.action?.kind === "company_login" && !wybor ? (
                      <Button onClick={() => void zalogujAdministratora()} disabled={busy} arrow>
                        {firma.action.label}
                      </Button>
                    ) : null}
                    {wybor?.map((w) => (
                      <Button key={w.rodzaj} onClick={() => void zalogujAdministratora(w.rodzaj)} disabled={busy} arrow>
                        {w.label}
                      </Button>
                    ))}
                    {wybor ? (
                      // Powrót z wyboru konta do poprzedniego kroku logowania.
                      <Button variant="ghost" onClick={() => setWybor(null)} disabled={busy} data-testid="firma-wybor-wstecz">
                        {t.wizBack}
                      </Button>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}

          {step === 1 ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <div className="ld-slug">
                <TextInput
                  label={t.fSlug}
                  mono
                  placeholder="twoja-firma"
                  value={form.slug}
                  error={errors.slug}
                  onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/\s+/g, "-") })}
                />
                <span className="ld-slug__suffix">.{ROOT_DOMAIN}</span>
              </div>
              <span className="ui-field__hint">{t.slugHint}</span>
              {form.slug ? (
                slug.checking ? (
                  <span className="ld-slug__status">{t.slugChecking}</span>
                ) : slug.available === true ? (
                  <span className="ld-slug__status" data-ok="true">
                    {t.slugFree} — {slug.host}
                  </span>
                ) : slug.available === false ? (
                  <span className="ld-slug__status" data-ok="false">
                    {slug.reason === "taken" ? t.slugTaken : t.slugInvalid}
                  </span>
                ) : null
              ) : null}
            </div>
          ) : null}

          {step === 2 ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <span className="ui-field__hint">{t.industriesHint}</span>
              <div className="ld-grid-cats">
                {industries.map((i) => (
                  <Tile
                    key={i.key}
                    title={lang === "en" ? (i.labelEn ?? i.label) : i.label}
                    icon={<Icon name={ICON_FOR[i.key] ?? "plus"} size={26} />}
                    color={CAT_COLOR[i.key] ?? EXTRA_COLOR[i.key] ?? "#6B6F76"}
                    selected={form.industries.includes(i.key)}
                    onSelect={() =>
                      setForm({
                        ...form,
                        industries: form.industries.includes(i.key)
                          ? form.industries.filter((x) => x !== i.key)
                          : [...form.industries, i.key],
                      })
                    }
                  />
                ))}
              </div>
              {errors.industries ? <span className="ui-field__error">{errors.industries}</span> : null}
            </div>
          ) : null}

          {step === 3 ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <span className="ui-field__hint">{t.planHint}</span>
              <div className="ld-grid-4">
                {plans.map((p, i) => (
                  <Card
                    key={p.id}
                    hover
                    accent={form.plan === p.id}
                    onClick={() => setForm({ ...form, plan: p.id })}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="ld-plan">
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span className="ld-plan__name">{p.id}</span>
                        {p.recommended ? <Chip variant="tag">{t.recommended}</Chip> : null}
                      </div>
                      <div className="ld-plan__ops">
                        {p.range} {t.opsSuffix}
                      </div>
                      <div className="ld-plan__price">
                        {p.formatted ?? t.custom}
                        {p.formatted ? <small>{" "}/{t.month}</small> : null}
                      </div>
                      <div className="ld-plan__features">
                        <span>{t[`t${i + 1}f1` as keyof typeof t]}</span>
                        <span>{t[`t${i + 1}f2` as keyof typeof t]}</span>
                        <span>{t[`t${i + 1}f3` as keyof typeof t]}</span>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
              {errors.plan ? <span className="ui-field__error">{errors.plan}</span> : null}
            </div>
          ) : null}

          {step === 4 ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div className="ld-fields">
                <TextInput
                  label={t.fName}
                  value={form.admin.name}
                  error={errors.name}
                  onChange={(e) => setAdmin({ name: e.target.value })}
                />
                <TextInput
                  label={t.fEmail}
                  mono
                  type="email"
                  value={form.admin.email}
                  error={errors.email}
                  readOnly={dowod !== null}
                  onChange={(e) => setAdmin({ email: e.target.value })}
                />
                {bezHasla ? null : (
                  <>
                    <TextInput
                      label={t.fPass}
                      type="password"
                      mono
                      value={form.admin.pass}
                      error={errors.pass}
                      onChange={(e) => setAdmin({ pass: e.target.value })}
                    />
                    <TextInput
                      label={t.fPass2}
                      type="password"
                      mono
                      value={form.admin.pass2}
                      error={errors.pass2}
                      onChange={(e) => setAdmin({ pass2: e.target.value })}
                    />
                  </>
                )}
              </div>
              <Checkbox
                label={t.fTerms}
                error={errors.terms}
                checked={form.admin.terms}
                onChange={(e) => setAdmin({ terms: e.target.checked })}
              />
            </div>
          ) : null}

          {step === 5 ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <b style={{ fontSize: 16 }}>{t.payTitle}</b>
              <span className="ui-field__hint">{t.payHint}</span>
              <Checkbox
                label={t.payEnable}
                checked={form.payments.enabled}
                onChange={(e) => setForm({ ...form, payments: { ...form.payments, enabled: e.target.checked } })}
              />
              {form.payments.enabled ? (
                <>
                  <span className="ui-field__label">{t.payMethods}</span>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {PAY_METHODS.map((m) => (
                      <TogglePill
                        key={m.id}
                        active={form.payments.methods.includes(m.id)}
                        onToggle={() =>
                          setForm({
                            ...form,
                            payments: {
                              ...form.payments,
                              methods: form.payments.methods.includes(m.id)
                                ? form.payments.methods.filter((x) => x !== m.id)
                                : [...form.payments.methods, m.id],
                            },
                          })
                        }
                      >
                        {m.label}
                      </TogglePill>
                    ))}
                  </div>
                </>
              ) : null}
            </div>
          ) : null}

          {step === 6 ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <b style={{ fontSize: 16 }}>{t.sumTitle}</b>
              <dl className="ld-sum">
                <dt>{t.sumCompany}</dt>
                <dd>
                  {form.companyName} · NIP {form.nip}
                  <br />
                  <span className="b00k-muted" style={{ fontWeight: 400 }}>
                    {form.address} · {form.phone}
                  </span>
                </dd>
                <dt>{t.sumSub}</dt>
                <dd className="b00k-mono">
                  {form.slug}.{ROOT_DOMAIN}
                </dd>
                <dt>{t.sumIndustries}</dt>
                <dd>
                  {form.industries
                    .map((k) => {
                      const i = industries.find((x) => x.key === k);
                      return i ? (lang === "en" ? (i.labelEn ?? i.label) : i.label) : k;
                    })
                    .join(", ")}
                </dd>
                <dt>{t.sumPlan}</dt>
                <dd>{form.plan}</dd>
                <dt>{t.sumAdmin}</dt>
                <dd>
                  {form.admin.name} · <span className="b00k-mono">{form.admin.email}</span>
                </dd>
                <dt>{t.sumPayments}</dt>
                <dd>
                  {form.payments.enabled ? `${t.yes} · ${form.payments.methods.join(", ").toUpperCase()}` : t.no}
                </dd>
              </dl>
            </div>
          ) : null}
        </Card>
      </WizardStepper>
    </main>
  );
}
